import cx from 'clsx';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { Suspense, useState } from 'react';

import { ErrorBoundaryWrapper } from '#core/components/error/ErrorBoundaryWrapper.tsx';
import { FullscreenToggleButton } from '#core/components/layout/FullscreenToggleButton.tsx';

import classes from './DashboardGrid.module.css';
import { BoxSkeleton } from './panels/shared/BoxSkeleton.tsx';
import { itemTransition } from './panels/shared/constants.ts';
import type { DashboardGridItem } from './reconciler/serialize.ts';
import { getComponent, getComponentSkeleton } from './registry.ts';
import { ValidationError } from './validation-error.ts';

export interface GridItemRendererProps {
  item: DashboardGridItem;
  /** Custom content renderer (bypasses registry lookup) */
  renderContent?: ReactNode;
}

export function GridItemRenderer({ item, renderContent }: GridItemRendererProps) {
  const { position, component, caption, captionPosition = 'left', layer = false } = item;
  const [fullscreenTarget, setFullscreenTarget] = useState<HTMLDivElement | null>(null);

  const componentType = component?.type != null && typeof component.type === 'string' ? component.type : 'unknown';

  const itemStyle: React.CSSProperties = {
    gridRow: `${position.row} / span ${position.rowSpan}`,
    gridColumn: `${position.column} / span ${position.columnSpan}`,
  };

  const content = (() => {
    if (renderContent !== undefined) {
      return renderContent;
    }

    if (component == null || componentType === 'unknown') {
      return <BoxSkeleton />;
    }

    if (typeof component.type !== 'string') {
      throw new Error(`DashboardGrid: Component type must be a string, got ${typeof component.type}`);
    }

    const entry = getComponent(componentType);
    if (!entry) {
      throw new Error(`DashboardGrid: Component type "${componentType}" not found in registry`);
    }

    const validation = entry.schema.safeParse(component);
    if (!validation.success) {
      const issues = validation.error.issues.map((issue) => ({
        path: issue.path.join('.') || 'root',
        message: issue.message,
      }));
      throw new ValidationError(componentType, issues);
    }

    const Component = entry.component;
    const validatedProps = validation.data;
    const ComponentSkeleton = getComponentSkeleton(componentType) ?? BoxSkeleton;

    return (
      <Suspense fallback={<ComponentSkeleton />}>
        <Component {...validatedProps} />
      </Suspense>
    );
  })();

  return (
    <motion.div
      className={cx(classes.gridItem, layer && classes.layer)}
      data-testid="grid-item"
      role="gridcell"
      style={{ ...itemStyle, originX: 0.5, originY: 0.5 }}
      variants={itemTransition}
    >
      {caption && (
        <div className={cx(classes.caption, classes[captionPosition])}>
          <div className={classes.captionText}>{caption}</div>
        </div>
      )}
      <div className={classes.itemContent} ref={setFullscreenTarget}>
        <ErrorBoundaryWrapper
          className={classes.gridItemError}
          context="DashboardGrid.Cell"
          metadata={{ componentType }}
          title={`Failed to render ${componentType}`}
          variant="inline"
        >
          {content}
        </ErrorBoundaryWrapper>
        <FullscreenToggleButton target={fullscreenTarget} />
      </div>
    </motion.div>
  );
}
